import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Send,
  Package,
  Paperclip,
  CheckCheck,
  Building2,
  Clock,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../store/slices/authSlice";
import styles from "./PurchaseOrderChat.module.css";

const POLL_INTERVAL_MS = 4000;

function normalizeMessage(raw, myRole) {
  const text = raw.body ?? raw.message ?? raw.text ?? "";
  const senderType = raw.sender_type ?? raw.type ?? raw.from ?? "customer";
  const createdAt = raw.created_at ?? raw.createdAt ?? raw.timestamp ?? null;
  return {
    id: raw.id ?? raw._id ?? `${createdAt}-${Math.random()}`,
    text,
    senderType,
    isMine: senderType === myRole,
    senderName: raw.sender_name ?? raw.sender?.name ?? null,
    attachmentUrl: raw.attachment_url ?? raw.image_url ?? null,
    createdAt,
  };
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function PurchaseOrderChat() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const MY_ROLE = "user";

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const listRef = useRef(null);
  const pollRef = useRef(null);
  const isFirstLoad = useRef(true);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback((smooth = true) => {
    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: smooth ? "smooth" : "auto",
        });
      }
    });
  }, []);

  const fetchMessages = useCallback(
    async (isBackground = false) => {
      try {
        const res = await api.get(
          `/supplier-materials/purchase-orders/${id}/chat/messages`,
        );
        if (res.data.code === 200) {
          const raw = res.data.data?.messages || [];
          const normalized = raw
            .map((m) => normalizeMessage(m, MY_ROLE))
            .reverse();
          setMessages(normalized);
          if (!isBackground) scrollToBottom(false);
        }
      } catch (err) {
        if (!isBackground) {
          setError(
            t(
              "chat.errorLoad",
              "Could not load the conversation. Please try again.",
            ),
          );
        }
      } finally {
        if (isFirstLoad.current) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    },
    [id, t, scrollToBottom],
  );

  useEffect(() => {
    fetchMessages(false);
    pollRef.current = setInterval(() => fetchMessages(true), POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [id, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith("image/")) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if ((!trimmed && !selectedFile) || sending) return;

    setSending(true);
    setError(null);

    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      text: trimmed,
      senderType: MY_ROLE,
      isMine: true,
      senderName: null,
      attachmentUrl: filePreview,
      createdAt: new Date().toISOString(),
      pending: true,
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setText("");
    removeSelectedFile();

    try {
      const payload = { body: trimmed };
      const res = await api.post(
        `/supplier-materials/purchase-orders/${id}/chat/messages`,
        payload,
      );

      if (res.data.code === 200 || res.data.code === 201) {
        const sent = normalizeMessage(res.data.data, MY_ROLE);
        setMessages((prev) =>
          prev.filter((m) => m.id !== optimisticId).concat(sent),
        );
      } else {
        throw new Error("send_failed");
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      const backendErrors = err.response?.data?.data;
      const firstBackendError =
        backendErrors && typeof backendErrors === "object"
          ? Object.values(backendErrors)[0]?.[0]
          : null;
      setError(
        firstBackendError ||
          err.response?.data?.message ||
          t("chat.errorSend", "Message could not be sent. Please try again."),
      );
      setText(trimmed);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={styles.pageLayout} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.chatContainer}>
        <header className={styles.chatHeader}>
          <div className={styles.headerLeft}>
            <button
              className={styles.backBtn}
              onClick={() => navigate(-1)}
              title={t("common.back", "Back")}
            >
              {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
            </button>
            <div className={styles.avatarWrap}>
              <Building2 size={20} className={styles.avatarIcon} />
              <span className={styles.statusBadge} />
            </div>
            <div className={styles.headerInfo}>
              <div className={styles.titleRow}>
                <h1 className={styles.supplierTitle}>
                  {t("chat.title", "Supplier Support")}
                </h1>
                <span className={styles.orderTag}>
                  <Package size={12} />
                  #PO-{id}
                </span>
              </div>
              <p className={styles.subtitle}>
                <Clock size={12} />
                {t("chat.responseTime", "Typically replies within a few hours")}
              </p>
            </div>
          </div>
        </header>

        <div className={styles.messagesWrap} ref={listRef}>
          {loading ? (
            <div className={styles.skeletonList}>
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className={`${styles.skeletonBubble} ${
                    i % 2 === 0 ? styles.skeletonLeft : styles.skeletonRight
                  }`}
                />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIconBg}>
                <Package size={32} strokeWidth={1.5} />
              </div>
              <h3>{t("chat.noMessagesTitle", "No messages yet")}</h3>
              <p>
                {t(
                  "chat.empty",
                  "Direct communication with supplier regarding purchase order.",
                )}
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`${styles.bubbleRow} ${
                  msg.isMine ? styles.bubbleRowMine : styles.bubbleRowTheirs
                }`}
              >
                <div
                  className={`${styles.bubble} ${
                    msg.isMine ? styles.bubbleMine : styles.bubbleTheirs
                  } ${msg.pending ? styles.bubblePending : ""}`}
                >
                  {!msg.isMine && msg.senderName && (
                    <span className={styles.bubbleSender}>
                      {msg.senderName}
                    </span>
                  )}

                  {msg.attachmentUrl && (
                    <div className={styles.attachmentPreview}>
                      <img
                        src={msg.attachmentUrl}
                        alt="attachment"
                        className={styles.attachedImage}
                      />
                    </div>
                  )}

                  {msg.text && <p className={styles.bubbleText}>{msg.text}</p>}

                  <div className={styles.bubbleFooter}>
                    <span className={styles.bubbleTime}>
                      {formatTime(msg.createdAt)}
                    </span>
                    {msg.isMine && (
                      <CheckCheck
                        size={14}
                        className={`${styles.checkIcon} ${
                          msg.pending ? styles.pendingCheck : styles.sentCheck
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.inputArea}>
          {selectedFile && (
            <div className={styles.fileBar}>
              <div className={styles.fileInfo}>
                {filePreview ? (
                  <img src={filePreview} alt="thumb" className={styles.thumb} />
                ) : (
                  <Paperclip size={16} />
                )}
                <span className={styles.fileName}>{selectedFile.name}</span>
              </div>
              <button
                type="button"
                className={styles.removeFileBtn}
                onClick={removeSelectedFile}
              >
                <X size={14} />
              </button>
            </div>
          )}

          <form className={styles.inputForm} onSubmit={handleSend}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept="image/*,.pdf,.doc,.docx"
            />

            <button
              type="button"
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              title={t("chat.attach", "Attach file")}
            >
              <Paperclip size={19} />
            </button>

            <textarea
              className={styles.textarea}
              placeholder={t("chat.placeholder", "Type your message...")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              rows={1}
            />

            <button
              type="submit"
              className={styles.sendBtn}
              disabled={sending || (!text.trim() && !selectedFile)}
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
